<template>
  <div class="hero-section-layout" :class="[$config.project, { locked: locked && $config.project === 'lms' }]">
    <div class="container image-container">
      <div class="row">
        <div class="col" :class="$config.project === 'lms' ? 'col-6' : 'col-5 md-6'">
          <div class="heading">
            <h1 class="h3">
              <span v-if="locked && $config.project === 'lms'" class="locked"><LockedIcon /> Locked</span>
              {{ course.name }}
            </h1>
            <div class="instructor">
              <span v-for="(i, k) in course.instructorsArr" :key="k">
                {{ `${k === 0 ? 'with' : 'and'}` }}
                <a v-if="$config.platform === 'ds'" :href="`${$config.siteUrl}/instructors/${i.slug}`">{{ `${i.name}` }}</a>
                <span v-else>{{ `${i.name}` }}</span>
              </span>
              <Rating v-if="course.reviews" :reviews="course.reviews" />
            </div>
            <p
              v-if="course.shortDescription"
              class="body-1 description"
            >
              {{ course.shortDescription }}
            </p>
            <p v-if="locked && $config.project === 'lms'" class="locked-info">
              *
              <template v-if="course.unlockOrigin.level && course.unlockOrigin.collection">
                To unlock, complete the {{ course.unlockOrigin.collection }} collection or reach level {{ course.unlockOrigin.level }}
              </template>
              <template v-else-if="course.unlockOrigin.level">
                To unlock, reach level {{ course.unlockOrigin.level }}
              </template>
              <template v-else-if="course.unlockOrigin.collection">
                To unlock, complete the {{ course.unlockOrigin.collection }} collection
              </template>
              <template v-else-if="course.unlockOrigin.card">
                To unlock, acquire the {{ course.unlockOrigin.card }} card
              </template>
            </p>
            <div v-if="!course.progress && !course.certificate && !course.started" class="info">
              <span>{{ course.totalHours }} {{ course.totalHours === 1 ? 'hour' : 'hours' }}</span>
              <span class="dot" />
              <span>{{ course.lessons.total }} lessons</span>
            </div>
            <template v-if="course.status === 'public' || (course.status === 'locked' && course.unlocked) || $config.project === 'site' ">
              <nuxt-link
                v-if="$store.getters['auth/user'].role === 'business-admin' && $route.name === 'business-library-slug'"
                class="btn-primary" :to="`/business/tasks/new/?course=${course.id}`"
              >
                Assign
              </nuxt-link>
              <template v-else>
                <Progress
                  v-if="$config.project === 'lms' && (course.progress || course.certificate || course.started)"
                  :progress="course.progress"
                  :data="course.lessons"
                  :certificate="course.certificate"
                />

                <button v-if="course.examId && course.progress === 100 && !course.examAllowed && !course.certificate" class="action-button retake-exam btn-primary" :to="buttonLink">
                  <SubtractIcon /> Retake exam

                  <div class="tooltip">
                    Remaining watch time until exam is unlocked: {{ course.examWatchTillUnlock }} min
                  </div>
                </button>
                <nuxt-link
                  v-else class="action-button btn-primary"
                  :class="{ exams: buttonLink.includes('exams'), disabled: buttonDisabled }"
                  :to="buttonLink"
                  data-event="start_now.click"
                >
                  {{ buttonText }}
                </nuxt-link>

                <div v-if="buttonLink.includes('exams') || (course.progress === 100 && !course.examAllowed)" class="mobile-warning">
                  <WarningIcon /> To attempt the exams please switch to a desktop device.
                </div>

                <nuxt-link v-if="course.certificate && course.progress !== 100" class="action-button btn-primary-transparent" :to="`/courses/${course.slug}/`">
                  {{ secondButtonText }}
                </nuxt-link>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="wall" :style="{ backgroundImage: `url(${$webp(`${$config.imagesUrl}/courses/${course.bigImage}`)})` }" />
  </div>
</template>

<script>
import Progress from '../Progress';
import Rating from '../Rating';
import WarningIcon from '../../assets/svg/icon-exam-warning.svg?vue-component';
import SubtractIcon from '../../assets/svg/icon-exams-subtract.svg?vue-component';
import LockedIcon from '../../assets/svg/icon-locked.svg?vue-component';

export default {
  components: {
    Progress,
    Rating,
    WarningIcon,
    SubtractIcon,
    LockedIcon,
  },
  props: {
    course: { type: Object, required: true },
    buttonDisabled: { type: Boolean, required: false },
  },
  computed: {
    buttonText() {
      if (this.course.certificate) {
        return 'Course certificate';
      }

      if (this.course.progress === 100) {
        return 'Course exam';
      }

      if (this.course.progress || this.course.started) {
        return 'Continue watching';
      }

      return 'Start course';
    },
    buttonLink() {
      if (this.course.certificate) {
        return `/certificates/${this.course.certificate}/`;
      }

      if (this.course.progress === 100) {
        if (this.course.examAllowed) {
          return `/exams/${this.course.examId}/instructions/`;
        }

        return '/exams/?tab=course';
      }

      if (this.$config.project !== 'lms' && !this.course.progress) {
        return '/signup/';
      }

      return `/courses/${this.course.slug}/`;
    },
    secondButtonText() {
      if (this.course.progress) {
        return 'Continue watching';
      }

      return 'Start watching';
    },
    locked() {
      return this.course.status === 'locked' && !this.course.unlocked;
    },
  },
};
</script>

<style lang="scss">
.hero-section-layout {
  background-color: var(--main-dark);
  position: relative;
  overflow: hidden;

  &.locked .wall {
    mix-blend-mode: luminosity;
  }

  .wall {
    position: absolute;
    right: 0;
    top: 0;
    height: 650px;
    left: 0;
    z-index: 0;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    image-rendering: -webkit-optimize-contrast;

    &:after {
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      content: '';
      left: -325px;
      height: 650px;
      top: 0;
      position: absolute;
      width: 650px;
      z-index: 0;
    }

    img {
      height: 650px;
    }
  }

  &:before,
  &:after {
    content: '';
    left: 0;
    position: absolute;
    width: 100%;
    z-index: 2;
  }

  &:before {
    background: linear-gradient(180deg, rgba(0, 12, 31, 0.8) 0%, rgba(0, 12, 31, 0) 100%);
    background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
    height: 110px;
    top: 0;
  }

  &:after {
    bottom: 0;
    background: linear-gradient(360deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
    height: 200px;
  }

  .mobile-warning {
    display: none;
  }

  .container {
    align-items: center;
    min-height: 380px;
    padding-top: 80px;

    &.image-container {
      height: 650px;
    }

    .row {
      align-items: center;
      height: 100%;

      > div {
        height: inherit;
        display: flex;
        align-items: center;
        position: relative;
        color: var(--main-light);
        z-index: 5;
        min-height: 380px;

        h1 {
          margin: 0 auto var(--spacing-16);
        }

        .description {
          margin: 0 0 var(--spacing-28);
          color: var(--main-light-shade-4);
        }

        .instructor {
          font-weight: 500;
          font-size: 18px;
          margin: 0;

          .course-rating {
            margin-top: var(--spacing-8);
            margin-bottom: var(--spacing-28);
          }

          &.site {
            margin-bottom: var(--spacing-48);
          }

          a {
            color: inherit;

            &:hover {
              color: var(--main-light-shade-4);
            }
          }
        }

        .btn-primary {
          margin-top: var(--spacing-48);
          max-width: 300px;
          width: 100%;
          height: 58px;
          padding: var(--spacing-20) 0;
        }

        .info {
          font-weight: 500;
          font-size: 14px;
          display: flex;
          align-items: center;
          margin-top: 16px;
          margin-bottom: 28px;

          span.dot {
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: var(--main-light);
            display: inline-block;
            margin: 0 8px;
          }
        }

        .heading {
          .locked {
            background-color: var(--main-red);
            border-radius: 5px;
            color: var(--main-light);
            font-size: 14px;
            padding: 4px 8px;
            display: flex;
            align-items: center;
            line-height: 24px;
            max-width: 90px;
            margin-bottom: 8px;

            svg {
              width: 17px;
              margin-right: 4px;
            }
          }
        }

        .heading .action-button {
          &.btn-primary {
            margin-top: 0;
            margin-right: var(--spacing-12);
          }

          &.retake-exam {
            display: flex;
            justify-content: center;
            color: var(--main-light-shade-4);
            background: transparent;
            border-color: var(--main-light-shade-4);
            align-items: center;
            position: relative;

            svg {
              margin-right: var(--spacing-8);
            }

            &:hover {
              border-color: var(--main-dark-light-4);
              color: var(--main-dark-light-4);

              svg path {
                fill: var(--main-dark-light-4);
              }

              .tooltip {
                display: block;
              }
            }

            .tooltip {
              display: none;
              position: absolute;
              bottom: 120%;
              background: rgba(22, 25, 29, 0.8);
              border: 1px solid var(--main-dark);
              border-radius: 5px;
              font-size: 12px;
              line-height: 20px;
              color: var(--main-light);
              font-weight: normal;
              left: 50%;
              width: 180px;
              padding: var(--spacing-4) var(--spacing-8);
              transform: translateX(-50%);
              white-space: normal;
              z-index: 1;

              &:after {
                content: "";
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-top: 5px solid #16191D;
                position: absolute;
                top: 100%;
                left: 50%;
              }
            }
          }
        }
      }
    }
  }

  &.lms {
    .container {
      padding-top: 0;

      &.image-container {
        height: 544px;
      }

      .row {
        > div {
          h1 {
            margin: 0 auto var(--spacing-8);
          }

          .description {
            color: var(--main-light-shade-4);
            font-size: 16px;
          }

          .course-progress {
            margin-top: var(--spacing-28);
            margin-bottom: var(--spacing-32);
          }

          .instructor {
            margin: 0 auto var(--spacing-24);
            font-weight: bold;
            font-size: 16px;
          }

          .btn-primary {
            margin-top: var(--spacing-28);
          }

          .locked-info {
            font-size: 12px;
            font-weight: 500;
            line-height: 20px;
            border-left: 2px solid var(--main-red);
            padding-left: 12px;
          }

          .heading .action-button {
            &.btn-primary,
            &.btn-primary-transparent {
              height: 40px;
              padding: 11px 1.25em;
              width: 224px;
            }

            &.disabled {
              pointer-events: none;
              opacity: 0.3;
            }
          }
        }
      }
    }

    .wall {
      height: 544px;

      &:after {
        height: 544px;
      }
    }
  }

  @include bp($bp-laptop) {
    &.lms .container .row > div h1 {
      font-size: 32px;
    }

    .container .row > div .heading .action-button.btn-primary {
      max-width: 225px;
    }
  }

  @include bp($bp-mobile) {
    &:before {
      background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0.76) 37.5%, rgba(0, 12, 31, 0) 100%);
      height: 100px;
    }

    &:after {
      display: none;
    }

    .wall {
      background-position: 77%;
      background-repeat: no-repeat;
      background-size: cover;
      height: 450px;
      width: 100%;

      img {
        height: 450px;
        left: 0;
        transform: translateX(-50%);
      }

      &:after {
        background: linear-gradient(0deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
        bottom: auto;
        top: 320px;
        left: 0;
        height: 130px;
        width: 100%;
      }
    }

    .container.image-container {
      height: 100% !important;

      .row > div {
        align-items: flex-end;
        padding-bottom: 30px;
      }

      .description {
        font-size: 16px;
      }

      .instructor {
        margin-bottom: var(--spacing-8) !important;
      }
    }

    &.site {
      height: 869px;

      .container .row > div {
        padding-bottom: 80px;
      }
    }

    &.lms {
      height: 748px;

      .container.image-container {
        .row > div h1 {
          font-size: 38px;
          line-height: 45px;
        }
      }

      .wall {
        height: 450px;

        &:after {
          height: 130px;
        }
      }
    }

    .heading {
      .action-button {
        &.exams,
        &.retake-exam {
          pointer-events: none;
          opacity: 0.3;
        }
      }
    }

    .mobile-warning {
      border: 1px solid var(--main-light-shade-4);
      border-radius: 10px;
      padding: var(--spacing-8);
      margin-top: var(--spacing-32);
      display: flex;

      svg {
        flex: 0 0 auto;
        margin-right: 10px;
        margin-top: 5px;

        > path:first-child {
          fill: var(--main-dark-light-4);
        }
      }
    }
  }

  @include bp ($bp-tablet) {
    .wall {
      background-size: 160%;
      background-position: 100%;
    }
  }

  @include bp($bp-phone) {
    &.lms {
      height: 100%;

      .container.image-container {
        padding-top: 320px;
      }
    }

    .container .row > div {
      a,
      .btn-primary {
        width: 100% !important;
      }

      .btn-primary-transparent {
        margin-top: 12px;
      }

      .action-button,
      .btn-primary,
      .btn-primary-transparent {
        max-width: 100%;
      }
    }
  }
}

@include bp($bp-laptop) {
  .course-preview-page {
    .course-preview-wrap.hero-section {
      .hero-section-layout.site {
        .wall {
          background-position: 62% center;
        }
      }
    }
  }
}

.fa .hero-section-layout.lms .container .row > div {
  .heading .btn-primary-transparent {
    border-color: var(--main-light);
    color: var(--main-light);

    &:hover {
      border-color: var(--main-light-shade-3);
      color: var(--main-light-shade-3);
      background: unset;
    }

    &:active {
      border-color: var(--main-light-shade-4);
      color: var(--main-light-shade-4);
      background: unset;
    }
  }

  .course-progress .progress-bar {
    background-color: var(--background-700);
    border-color: var(--main-dark-light-3);
  }
}
</style>
